import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Navbar.css';

const socialLinks = {
   instagram: 'https://instagram.com/ye___ra',
   youtube: 'https://youtube.com/@yerakim3956',
};

function NavigateBar({ username }) {
   const [isScrolled, setIsScrolled] = useState(false);
   const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
   const location = useLocation();
   const isHomePage = location.pathname === '/';

   const navItems = [
      { title: 'Biography', path: '/biography' },
      { title: 'Videos', path: '/videos' },
      // { title: 'Events', path: '/events' },  // Hidden for now
      { title: 'Contact', path: '/contact' },
   ];

   useEffect(() => {
      const handleScroll = () => {
         setIsScrolled(window.scrollY > 50);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
   }, []);

   useEffect(() => {
      setIsMobileMenuOpen(false);
   }, [location]);

   // Prevent body scroll when mobile menu is open
   useEffect(() => {
      if (isMobileMenuOpen) {
         document.body.style.overflow = 'hidden';
      } else {
         document.body.style.overflow = 'unset';
      }
      return () => {
         document.body.style.overflow = 'unset';
      };
   }, [isMobileMenuOpen]);

   const navBgClass = isScrolled || !isHomePage ? 'nav-solid' : 'nav-transparent';

   return (
      <>
         <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className={`navbar-custom ${navBgClass}`}
         >
            <div className="navbar-container">
               {/* Logo */}
               <Link to="/" className="navbar-logo">
                  {username}
               </Link>

               {/* Desktop Navigation */}
               <div className="navbar-desktop">
                  {navItems.map((item, index) => (
                     <Link
                        key={index}
                        to={item.path}
                        className={`navbar-link ${location.pathname === item.path ? 'active' : ''}`}
                     >
                        {item.title}
                     </Link>
                  ))}
               </div>

               {/* Mobile Menu Button */}
               <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="navbar-hamburger"
                  aria-label="Toggle menu"
               >
                  <span className={`hamburger-line ${isMobileMenuOpen ? 'open-1' : ''}`} />
                  <span className={`hamburger-line ${isMobileMenuOpen ? 'open-2' : ''}`} />
                  <span className={`hamburger-line ${isMobileMenuOpen ? 'open-3' : ''}`} />
               </button>
            </div>
         </motion.nav>

         {/* Mobile Menu */}
         <AnimatePresence>
            {isMobileMenuOpen && (
               <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mobile-menu"
               >
                  <div className="mobile-menu-content">
                     {navItems.map((item, index) => (
                        <motion.div
                           key={index}
                           initial={{ opacity: 0, y: 20 }}
                           animate={{ opacity: 1, y: 0 }}
                           transition={{ delay: index * 0.1 }}
                        >
                           <Link
                              to={item.path}
                              className={`mobile-link ${location.pathname === item.path ? 'active' : ''}`}
                           >
                              {item.title}
                           </Link>
                        </motion.div>
                     ))}

                     {/* Social Links */}
                     <motion.div
                        className="mobile-social-links"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: navItems.length * 0.1 + 0.1 }}
                     >
                        <a
                           href={socialLinks.instagram}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="mobile-social-icon"
                           aria-label="Instagram"
                        >
                           <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                           </svg>
                        </a>
                        <a
                           href={socialLinks.youtube}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="mobile-social-icon"
                           aria-label="YouTube"
                        >
                           <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                              <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                           </svg>
                        </a>
                     </motion.div>
                  </div>
               </motion.div>
            )}
         </AnimatePresence>
      </>
   );
}

export default NavigateBar;
